// Framework-agnostic watermark config model (see WATERMARK_PORTABLE_BUILD_GUIDE §3.1).
// Styling + which tokens to include only — NEVER the token values (resolved client-side),
// so this object is safe to store server-side and deliver on an authed payload.

export type WatermarkPart =
  | 'bankName' | 'userName' | 'employeeId' | 'userEmail' | 'date'
  | 'custom' | 'sessionId' | 'timestamp' | 'ipAddress';

export interface WatermarkConfig {
  enabled: boolean;
  apps: string[];              // app ids the mark applies to; [] = entire platform
  parts: WatermarkPart[];      // ordered
  customText: string;          // used when 'custom' is in parts
  separator: string;
  color: string;               // hex
  opacity: number;             // 0.02–0.30
  fontSize: number;            // 10–48 px
  fontWeight: 400 | 500 | 600 | 700;
  rotation: number;            // -90..90 deg
  gap: number;                 // 80–400 px tile spacing (density)
  scatter: boolean;            // seeded-random scatter vs rigid grid
  forensic: boolean;           // invisible tiled hash layer (attributes crops)
  selfHeal: boolean;           // re-render if removed/hidden via DevTools
  timestampRotate: boolean;    // refresh the 'timestamp' token every 30s
  tamperTelemetry: boolean;    // beacon repeated tampering to the audit trail
  penaltyBlur: boolean;        // DANGER — briefly blur page content on tamper (default off)
  forceLogout: boolean;        // DANGER — log out after repeated tampering (default off)
}

export const WATERMARK_DEFAULTS: WatermarkConfig = {
  enabled: false,
  apps: [],
  parts: ['bankName', 'userName', 'employeeId'],
  customText: '',
  separator: ' · ',
  color: '#334155',
  opacity: 0.08,
  fontSize: 16,
  fontWeight: 500,
  rotation: -30,
  gap: 180,
  scatter: false,
  forensic: true,
  selfHeal: true,
  timestampRotate: true,
  tamperTelemetry: true,
  penaltyBlur: false,
  forceLogout: false,
};

export const WATERMARK_PARTS: WatermarkPart[] =
  ['bankName', 'userName', 'employeeId', 'userEmail', 'date', 'sessionId', 'timestamp', 'ipAddress', 'custom'];

export const WATERMARK_PART_LABELS: Record<WatermarkPart, string> = {
  bankName: 'App / company name', userName: 'User name', employeeId: 'Employee ID',
  userEmail: 'User email', date: 'Date', sessionId: 'Session ID',
  timestamp: 'Timestamp', ipAddress: 'IP address', custom: 'Custom text',
};

/** Merge a partial (possibly `{}`) stored config with defaults. */
export function mergeWatermarkConfig(stored: Partial<WatermarkConfig> | null | undefined): WatermarkConfig {
  return { ...WATERMARK_DEFAULTS, ...(stored ?? {}) };
}
